<template>
  <div>
    <ul class="nav nav-actions" v-if="canPerformUserActions && accountUserDecorated.isNotArchived && accountUserDecorated.isActivated">
      <li v-if="accountUserDecorated.isEnabled">
        <a @click="handleDisable">
          <i class="fa fa-circle-o-notch fa-spin fa-fw" v-if="performingUserDisabling"></i>
          <i class="fa fa-times fa-fw" v-else></i>
          Disable User
        </a>
      </li>
      <li v-if="accountUserDecorated.isDisabled">
        <a @click="handleEnable">
          <i class="fa fa-circle-o-notch fa-spin fa-fw" v-if="performingUserEnabling"></i>
          <i class="fa fa-refresh fa-fw" v-else></i>
          Enable User
        </a>
      </li>
      <li v-if="accountUserDecorated.isNotArchived">
        <a @click="handleArchive">
          <i class="fa fa-circle-o-notch fa-spin fa-fw" v-if="performingUserArchive"></i>
          <i class="fa fa-archive fa-fw" v-else></i>
          Archive User
        </a>
      </li>
    </ul>
    <user-details :can-change-role="false">
      <div slot="cropper">
        <photo-cropper
          v-if="loggedInUserIsAdmin"
          v-model="photo"
          name="driver"
          :photo-storage-path="accountUser.driver_profile.photo_storage_path"
          :original-name="accountUser.driver_profile.photo_name"
          :original-data-uri="originalPhotoDataUri"
          class-name="profile-photo"
          label-text="Profile Photo">
        </photo-cropper>

        <div v-else-if="accountUser.driver_profile && accountUser.driver_profile.photo_storage_path" class="preview">
          <img class="profile-photo" :src="`${accountUser.driver_profile.photo_storage_path}/256x256.jpg`">
        </div>
      </div>
    </user-details>

    <ladda-button :on-click="saveDriver" class="btn btn-success"><i class="fa fa-check"></i> Save</ladda-button>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
  import { RESET_ACCOUNT_USER, SET_ACCOUNT_USER_ATTRIBUTE, SET_ACCOUNT_USER } from 'vx/mutations'
  import { LaddaButton, PhotoCropper } from 'components/forms'
  import { stateMapper } from 'mixins/user'
  import UserDetails from './../user/UserDetails'
  import notify from 'utils/notify'

  export default {
    mixins: [stateMapper],

    data () {
      return {
        stateProperty: 'accountUser',
        mutationProperty: SET_ACCOUNT_USER_ATTRIBUTE,
        photo: null,
        originalPhotoDataUri: null,
        performingUserArchive: false,
        performingUserEnabling: false,
        performingUserDisabling: false
      }
    },

    components: {
      UserDetails,
      LaddaButton,
      PhotoCropper
    },

    computed: {
      ...mapState('user/accountUsers', ['accountUser']),
      ...mapGetters('user/accountUsers', ['accountUserDecorated']),

      loggedInUserIsAdmin () {
        return this.$user.isAdmin
      },

      /**
       * Determine if we can perform user actions against the user being edited.
       *
       * @return boolean
       */
      canPerformUserActions () {
        return this.$user.isAdminOrCoordinator && (this.accountUserDecorated.isClientOrCarrier || this.accountUserDecorated.isDriver)
      },

      /**
       * Get and set a users name on their profile.
       */
      name: {
        get () {
          return this.accountUser.profile.full_name
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'profile.full_name', value })
        }
      },

      /**
       * Payload to send with request.
       *
       * @return object
       */
      payload () {
        return {
          name: this.name,
          email: this.email,
          mobile: this.mobile,
          licence: this.licence,
          licence_expiry: this.licenceExpiry
        }
      }
    },

    created () {
      this.attemptToLoadExistingDriver()
    },

    methods: {
      ...mapActions('user', ['archiveUser', 'enableUser', 'disableUser']),
      ...mapActions('user/accountUsers', ['getAccountUser', 'storeAccountUser', 'updateAccountUser', 'removeUser']),
      ...mapActions('driver', ['getDriverPhotoDataUri', 'uploadDriverPhoto', 'updateDriver']),
      ...mapMutations('user/accountUsers', [RESET_ACCOUNT_USER, SET_ACCOUNT_USER_ATTRIBUTE, SET_ACCOUNT_USER]),

      /**
       * Attempt to load an existing driver.
       *
       * @return void
       */
      attemptToLoadExistingDriver () {
        this[RESET_ACCOUNT_USER]()

        const fetchOriginalPhotoDataUri = () => {
          if (this.accountUser.driver_id) {
            return this.getDriverPhotoDataUri({ id: this.accountUser.driver_id, dimension: 'original' }).then(response => {
              this.originalPhotoDataUri = response.data.data_uri
            })
          }
        }

        return this.getAccountUser(this.$route.params.id).then(fetchOriginalPhotoDataUri)
      },

      saveDriver () {
        return this.validateAll().then(() => {
          let payload = this.payload

          return this.updateDriver({ id: this.accountUser.id, payload }).then(this.handlePhotoUpload)
        })
      },

            /**
       * Handle uploading driver photo.
       *
       * @param object response
       *
       * @return Promise
       */
      handlePhotoUpload (response) {
        if (this.photo && response.data.driver_id) {
          return this.uploadDriverPhoto({ id: response.data.driver_id, file: this.photo.file, crop: this.photo.crop })
        }

        return Promise.resolve(response)
      },

      /**
       * Handle archiving a user.
       *
       * @return void
       */
      handleArchive () {
        if (this.performingUserArchive) {
          return
        }

        this.performingUserArchive = true

        const finishedPerformingArchiving = () => (this.performingUserArchive = false)
        const notifyArchived = () => (notify.success('User has been archived.'))

        this.archiveUser(this.accountUser.id).then(this.updateUserState).then(finishedPerformingArchiving).then(notifyArchived)
      },

      /**
       * Handle enabling a user.
       *
       * @return void
       */
      handleEnable () {
        if (this.performingUserEnabling) return

        this.performingUserEnabling = true

        const finishedPerformingEnabling = () => {
          this.performingUserEnabling = false

          notify.success('User has been enabled.')
        }

        const failedPerformingEnabling = () => {
          this.performingUserEnabling = false

          notify.error('Unable to enable the user.')
        }

        this.enableUser(this.accountUser.id).then(this.updateUserState).then(finishedPerformingEnabling).catch(failedPerformingEnabling)
      },

      /**
       * Handle disabling a user.
       *
       * @return void
       */
      handleDisable () {
        if (this.performingUserDisabling) return

        this.performingUserDisabling = true

        const finishedPerformingDisabling = () => {
          this.performingUserDisabling = false

          notify.success('User has been disabled.')
        }

        const failedPerformingDisabling = () => {
          this.performingUserDisabling = false

          notify.error('Unable to disable the user.')
        }

        this.disableUser(this.accountUser.id).then(this.updateUserState).then(finishedPerformingDisabling).catch(failedPerformingDisabling)
      },

      /**
       * Update a user state after a saving action.
       *
       * @param object response
       *
       * @return Promise
       */
      updateUserState (response) {
        this[SET_ACCOUNT_USER](response.data)

        return response
      }
    }
  }
</script>



// WEBPACK FOOTER //
// DriverForm.vue?2029a61e